import { useEffect, useRef, useState } from 'react';

type ImageOptions = {
  className?: string;
  display?: 'standard' | 'full-bleed';
  height?: Height;
  pad?: boolean;
  progressiveLoadSettings?: {
    highResQuality: number;
    lowResQuality: number;
  };
  quality?: number;
  retainRatio?: boolean;
  width?: Width;
};

type Width =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 357
  | 366
  | 380
  | 400
  | 414
  | 450
  | 474
  | 490
  | 576
  | 627
  | 672
  | 720
  | 768
  | 800
  | 900
  | 1000
  | 1024
  | 1160
  | 1440;

type Height =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 378
  | 380
  | 400
  | 414
  | 450
  | 474
  | 576
  | 653
  | 672
  | 768
  | 800
  | 1000
  | 1024
  | 1160;

type ResponsiveImageOptions = {
  height?: Height;
  pad?: boolean;
  quality?: number;
  retainRatio?: boolean;
  src?: string;
  width?: Width;
};

type ResponsiveImages = {
  lg?: ResponsiveImageOptions;
  md?: ResponsiveImageOptions;
  sm?: ResponsiveImageOptions;
  xl?: ResponsiveImageOptions;
  xs?: ResponsiveImageOptions;
};

type Props = {
  alt: string | undefined;
  bypassOptimization?: boolean;
  caption?: string;
  key?: string;
  loading?: 'lazy' | 'eager';
  responsiveOptions?: ResponsiveImages;
  src: string;
} & ImageOptions;

export const smallImageOptions = ({
  pad
}: {
  pad: boolean;
}): Partial<Props> => {
  return {
    height: 300,
    pad,
    responsiveOptions: {
      lg: { height: 400, pad, width: 400 },
      sm: { height: 300, pad, width: 300 }
    },
    width: 300
  };
};

export function addResizerFlags(
  src: string,
  { width, height, quality, pad, retainRatio }: ImageOptions
) {
  if (!src) return;

  const flags = [];

  if (width) flags.push(`_w${width}`);
  if (height) flags.push(`_h${height}`);
  if (quality) flags.push(`_q${quality}`);
  if (pad) flags.push('_pad_');
  if (retainRatio) flags.push('_rr_');

  const extension = src.match(/\.[^.]+$/);

  return src.replace(`${extension}`, `${flags.join('')}${extension}.webp`);
}

const ResponsiveSource = ({
  src,
  minWidth,
  options
}: {
  minWidth: string;
  options?: ImageOptions;
  src: string;
}) => {
  if (!options) return null;

  return (
    <source
      srcSet={addResizerFlags(src, options)}
      media={`(min-width: ${minWidth})`}
      className="h-auto max-w-full"
    />
  );
};

const Image = ({
  display,
  caption,
  className = '',
  responsiveOptions: propResponsiveOptions,
  progressiveLoadSettings,
  alt,
  loading,
  bypassOptimization,
  ...propOptions
}: Props) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [lowQualityLoaded, setLowQualityLoaded] = useState(false);
  const [highQualityLoaded, setHighQualityLoaded] = useState(false);

  const responsiveOptions = { ...propResponsiveOptions };
  const options = { ...propOptions };

  if (progressiveLoadSettings) {
    const currentQuality = highQualityLoaded
      ? progressiveLoadSettings.highResQuality
      : progressiveLoadSettings.lowResQuality;

    Object.keys(responsiveOptions ?? {}).forEach((k) => {
      responsiveOptions![k as keyof ResponsiveImages]!.quality = currentQuality;
    });

    options.quality = currentQuality;
  }

  useEffect(() => {
    if (!progressiveLoadSettings) return;

    if (imgRef.current?.complete && !lowQualityLoaded) {
      setLowQualityLoaded(true);
    }
  }, [lowQualityLoaded, progressiveLoadSettings]);

  useEffect(() => {
    if (!lowQualityLoaded || !progressiveLoadSettings || !imgRef.current)
      return;

    const highResSrc = imgRef.current.currentSrc.replace(
      `_q${progressiveLoadSettings?.lowResQuality}`,
      `_q${progressiveLoadSettings?.highResQuality}`
    );

    let img: HTMLImageElement | null = new window.Image();
    img.src = highResSrc;
    img.onload = () => {
      setHighQualityLoaded(true);
    };

    return () => {
      img = null;
    };
  }, [lowQualityLoaded, progressiveLoadSettings]);

  const isStandardDisplay = display === 'standard';

  if (bypassOptimization) {
    return (
      <img
        src={options.src}
        alt={alt}
        className={className}
        loading={loading}
      />
    );
  }

  return (
    <div
      className={`flex flex-col ${className} ${
        isStandardDisplay ? 'max-w-2xl' : ''
      }`}
    >
      <picture className={`block ${className}`}>
        {responsiveOptions?.xl && (
          <ResponsiveSource
            src={responsiveOptions.xl.src ?? options.src}
            minWidth="1280px"
            options={responsiveOptions?.xl}
          />
        )}
        {responsiveOptions?.lg && (
          <ResponsiveSource
            src={responsiveOptions.lg.src ?? options.src}
            minWidth="1024px"
            options={responsiveOptions?.lg}
          />
        )}
        {responsiveOptions?.md && (
          <ResponsiveSource
            src={responsiveOptions.md.src ?? options.src}
            minWidth="768px"
            options={responsiveOptions?.md}
          />
        )}
        {responsiveOptions?.sm && (
          <ResponsiveSource
            src={responsiveOptions.sm.src ?? options.src}
            minWidth="640px"
            options={responsiveOptions?.sm}
          />
        )}
        {responsiveOptions?.xs && (
          <ResponsiveSource
            src={responsiveOptions.xs.src ?? options.src}
            minWidth="200px"
            options={responsiveOptions?.xs}
          />
        )}
        {!responsiveOptions?.xs && (
          <ResponsiveSource
            src={options.src}
            minWidth="200px"
            options={options}
          />
        )}
        <img
          onLoad={() => {
            if (lowQualityLoaded || !progressiveLoadSettings) return;

            setLowQualityLoaded(true);

            // NOTE: Due to a bug this event does not fire on Firefox based browsers - https://bugzilla.mozilla.org/show_bug.cgi?id=1823852
          }}
          ref={imgRef}
          className={className}
          src={options.src}
          loading={loading}
          alt={alt}
        />
      </picture>
      {Boolean(caption) && (
        <div className="relative">
          <div className="absolute -bottom-5 right-1 text-xs font-semibold text-light-grey">
            {caption}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ImageWrapper(props: Props) {
  return <Image key={props.alt ?? props.src} {...props} />;
}
